import { TooltipArrow } from '@radix-ui/react-tooltip'
import Link from 'next/link'
import { useRouter } from 'next/router'
import * as React from 'react'

import { sanitizeHtmlValue } from '@/lib/element-schema-sanitizers'
import useLocale from '@/hooks/use-locale'

import { PageActions, PageHeader, PageHeaderDescription, PageHeaderHeading } from '@/components/common/page-header'
import { Shell } from '@/components/common/shell'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

import { FORM_REGISTER_URL } from '@/constant/app'

interface SectionHeroModuleDetailProps {
  heading?: React.ReactNode
  description?: React.ReactNode
  buttonHeading?: React.ReactNode
  detailCourseUrl: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  course?: Record<string, any>
  isCourseEnrolled: boolean
}

export default function SectionHeroModuleDetail({
  heading,
  description,
  detailCourseUrl,
  course,
  isCourseEnrolled,
}: SectionHeroModuleDetailProps) {
  const { text, lang } = useLocale()
  const router = useRouter()

  return (
    <Shell className="px-4 sm:px-6 md:px-8 py-8 sm:py-12 md:py-16 lg:py-20 min-h-[520px] sm:min-h-[660px] md:min-h-[760px] relative overflow-hidden mt-0 sm:mt-8 md:mt-12 lg:bg-[url('/images/home/background-home-page.png')] lg:bg-contain lg:bg-center lg:bg-no-repeat">
      <PageHeader className="space-y-4 sm:space-y-6 md:space-y-8 text-center w-full items-center justify-center flex mx-auto mt-12">
        {typeof heading === 'string' || heading == null ? (
          <PageHeaderHeading
            as="h2"
            size="lg"
            className="tracking-normal text-3xl sm:text-4xl md:text-5xl lg:text-6xl/tight md:leading-tight text-center"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtmlValue((heading as string) || text.MAIN.INTRODUCTION.HEADING_2),
            }}
          />
        ) : (
          <PageHeaderHeading
            as="h2"
            size="lg"
            className="tracking-normal text-3xl sm:text-4xl md:text-5xl lg:text-6xl/tight md:leading-tight text-center"
          >
            {heading}
          </PageHeaderHeading>
        )}
        {typeof description === 'string' || description == null ? (
          <PageHeaderDescription
            className="text-center text-base sm:text-lg md:text-xl mx-auto max-w-[55.875rem]"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtmlValue((description as string) || text.MAIN.INTRODUCTION.HEADING_3),
            }}
          />
        ) : (
          <PageHeaderDescription className="text-center text-base sm:text-lg md:text-xl mx-auto max-w-[55.875rem]">
            {description}
          </PageHeaderDescription>
        )}
        <PageActions className="justify-center flex-col sm:flex-row gap-4 mt-6 sm:mt-8 md:mt-10 space-x-0 sm:space-x-4">
          <TooltipProvider>
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <Link
                  href={detailCourseUrl || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto"
                >
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full border-primary text-primary hover:text-primary/80 sm:w-auto"
                  >
                    <span>{text.MAIN.BODY.COURSE.DETAIL_COURSE}</span>
                  </Button>
                </Link>
              </TooltipTrigger>
              {lang === 'vi' && (
                <TooltipContent className="bg-black text-white text-start" side="top" align="center">
                  <p className="text-sm font-semibold">Chi tiết khóa học có gì?</p>
                  <p className="text-sm">Trong đây chúng mình sẽ có cụ thể về</p>
                  <p className="text-sm">nội dung khóa học, học phí, và những ưu đãi</p>
                  <p className="text-sm">có một không hai!</p>
                  <TooltipArrow className="fill-black" direction="top" />
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          <Button size="lg" asChild className="w-full sm:w-auto">
            {lang === 'vi' ? (
              <Link href={FORM_REGISTER_URL} target="_blank" rel="noopener noreferrer">
                {text.MAIN.INTRODUCTION.HEADING_4}
              </Link>
            ) : (
              <Link
                href={
                  isCourseEnrolled
                    ? '/my-courses'
                    : course?.id === 'mlops-1'
                      ? `/checkout?course=${course?.id}`
                      : router.asPath
                }
                rel="noopener noreferrer"
              >
                {isCourseEnrolled
                  ? text.MAIN.INTRODUCTION.HEADING_6
                  : course?.id === 'mlops-1'
                    ? text.MAIN.INTRODUCTION.HEADING_5
                    : text.MAIN.INTRODUCTION.HEADING_7}
              </Link>
            )}
          </Button>
        </PageActions>
      </PageHeader>
    </Shell>
  )
}
